import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../../supabaseClient";

const fetchFamilyVersions = async (baseId) => {
  const { data, error } = await supabase
    .from("payoutTable")
    .select("*")
    .or(`id.eq.${baseId},"baseID".eq.${baseId}`)
    .is("deletedAt", null)
    .order("version", { ascending: true });

  if (error) throw error;
  return data || [];
};

export const rootPayoutTableId = (table) => table?.baseID || table?.id || null;

export const tableIsOrgDefault = (table, defaultId) => {
  if (!table || !defaultId) return false;
  if (table.id === defaultId) return true;
  if (rootPayoutTableId(table) === defaultId) return true;
  if (table.latestVersion?.id === defaultId) return true;
  return Boolean(
    table.versions?.some((version) => version.id === defaultId)
  );
};

export const fetchDefaultPayoutTableId = async (organizationId) => {
  if (!organizationId) return null;
  const { data, error } = await supabase
    .from("organizations")
    .select("defaultPayoutTableId")
    .eq("id", organizationId)
    .single();
  if (error) throw error;
  return data?.defaultPayoutTableId || null;
};

export const setOrganizationDefaultPayoutTable = async (
  organizationId,
  tableId
) => {
  if (!organizationId || !tableId) {
    throw new Error("Pick a payout table first.");
  }
  const { error } = await supabase
    .from("organizations")
    .update({ defaultPayoutTableId: tableId })
    .eq("id", organizationId);
  if (error) throw error;
};

export const deletePayoutTableFamily = async ({ baseId, userId }) => {
  if (!baseId || !userId) {
    throw new Error("Could not delete this payout table.");
  }

  const { data: table, error: tableError } = await supabase
    .from("payoutTable")
    .select("id, baseID, organizationId, frozenForTournamentId")
    .eq("id", baseId)
    .is("deletedAt", null)
    .single();

  if (tableError) throw tableError;
  if (table.frozenForTournamentId) {
    throw new Error(
      "This copy belongs to a tournament. It cannot be deleted from here."
    );
  }

  const defaultId = await fetchDefaultPayoutTableId(table.organizationId);
  if (tableIsOrgDefault(table, defaultId)) {
    throw new Error("Pick another default before deleting this table.");
  }

  const versions = await fetchFamilyVersions(baseId);
  const versionIds = versions
    .filter((version) => !version.frozenForTournamentId)
    .map((version) => version.id);

  if (!versionIds.length) {
    throw new Error("Could not delete this payout table.");
  }

  const { data: deleted, error: deleteError } = await supabase
    .from("payoutTable")
    .update({
      deletedAt: new Date().toISOString(),
      deletedBy: userId,
    })
    .in("id", versionIds)
    .select("id");

  if (deleteError) throw deleteError;
  if (!deleted?.length) {
    throw new Error("Could not delete this payout table.");
  }

  if (versionIds.length > 0) {
    await supabase
      .from("tournaments")
      .update({ payoutTableId: null })
      .in("payoutTableId", versionIds);
  }
};

export const useOrgPayoutTables = (organizationId) => {
  const [tables, setTables] = useState([]);
  const [defaultPayoutTableId, setDefaultPayoutTableId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async ({ quiet = false } = {}) => {
    if (!organizationId) return;
    if (!quiet) setLoading(true);
    setError("");
    try {
      const { data: org, error: orgError } = await supabase
        .from("organizations")
        .select("defaultPayoutTableId")
        .eq("id", organizationId)
        .single();

      if (orgError) throw orgError;
      setDefaultPayoutTableId(org?.defaultPayoutTableId || null);

      const { data: roots, error: rootsError } = await supabase
        .from("payoutTable")
        .select("*")
        .eq("organizationId", organizationId)
        .eq("version", 1)
        .is("deletedAt", null)
        .is("frozenForTournamentId", null)
        .order("name");

      if (rootsError) throw rootsError;

      const { data: tournaments, error: tournamentError } = await supabase
        .from("tournaments")
        .select("id, name, payoutTableId")
        .eq("organizationID", organizationId)
        .eq("isDeleted", false);

      if (tournamentError) throw tournamentError;

      const families = await Promise.all(
        (roots || []).map(async (root) => {
          const versions = await fetchFamilyVersions(root.id);
          const latest = versions[versions.length - 1] || root;
          const versionIds = versions.map((version) => version.id);
          const usedBy = (tournaments || []).filter((tournament) =>
            versionIds.includes(tournament.payoutTableId)
          );
          return {
            ...root,
            versions,
            latestVersion: latest,
            tournamentCount: usedBy.length,
            tournaments: usedBy,
          };
        })
      );

      setTables(families);
    } catch (err) {
      console.error("Error loading payout tables:", err);
      setError("Failed to load payout tables");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createTable = async ({ name, userId }) => {
    const { data, error: createError } = await supabase
      .from("payoutTable")
      .insert({
        name: name || "Payout table",
        description: "",
        organizationId,
        createdBy: userId,
        version: 1,
      })
      .select()
      .single();

    if (createError) throw createError;
    await refresh();
    return data;
  };

  const deleteFamily = async ({ baseId, userId }) => {
    await deletePayoutTableFamily({ baseId, userId });
    await refresh();
  };

  const setDefaultTable = async (table) => {
    const tableId = rootPayoutTableId(table);
    if (!tableId) throw new Error("Pick a payout table first.");
    if (tableIsOrgDefault(table, defaultPayoutTableId)) return;
    await setOrganizationDefaultPayoutTable(organizationId, tableId);
    await refresh({ quiet: true });
  };

  return {
    tables,
    defaultPayoutTableId,
    loading,
    error,
    refresh,
    createTable,
    deleteFamily,
    setDefaultTable,
  };
};

export const loadPayoutTable = async (tableId) => {
  const { data: table, error: tableError } = await supabase
    .from("payoutTable")
    .select("*")
    .eq("id", tableId)
    .is("deletedAt", null)
    .single();

  if (tableError) throw tableError;

  const baseId = table.baseID || table.id;
  const versions = await fetchFamilyVersions(baseId);
  const { data: payouts, error: payoutsError } = await supabase
    .from("payoutData")
    .select("payoutQuantity, percentage, payoutOrder")
    .eq("payoutTableId", table.id)
    .order("payoutOrder", { ascending: true });

  if (payoutsError) throw payoutsError;

  return {
    table,
    baseId,
    versions,
    payoutData: payouts || [],
  };
};

export const listOrganizationPayoutTables = async (organizationId) => {
  if (!organizationId) return [];
  const { data, error } = await supabase
    .from("payoutTable")
    .select("id, name, description, organizationId")
    .eq("organizationId", organizationId)
    .eq("version", 1)
    .is("deletedAt", null)
    .is("frozenForTournamentId", null)
    .order("name");

  if (error) throw error;
  return data || [];
};

export const savePayoutTable = async ({
  currentTable,
  name,
  description,
  payoutData,
  recipe,
}) => {
  const tableId = currentTable.id;

  const { error: updateError } = await supabase
    .from("payoutTable")
    .update({ name, description, ...recipe })
    .eq("id", tableId);

  if (updateError) throw updateError;

  const { error: deleteError } = await supabase
    .from("payoutData")
    .delete()
    .eq("payoutTableId", tableId);

  if (deleteError) throw deleteError;

  if (payoutData.length > 0) {
    const rows = payoutData.map((row) => ({
      payoutTableId: tableId,
      payoutQuantity: row.payoutQuantity,
      percentage: row.percentage,
      payoutOrder: row.payoutOrder,
    }));
    const { error: insertError } = await supabase.from("payoutData").insert(rows);
    if (insertError) throw insertError;
  }

  return { ...currentTable, name, description, ...recipe };
};
